import React from "react";
import {
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  IconButton,
  useDisclosure,
  Link,
  Image,
  Text,
} from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { Link as ReactRouterLink } from "react-router-dom";
import {
  LEARN_ROUTE,
  TRAIN_ROUTE,
  PLAYLISTS_ROUTE,
  SETTINGS_ROUTE,
  LOGOUT_ROUTE,
} from "../../routes";
import { useLocaleContext } from "../../providers/LocaleProvider";
import { useSelector } from "../../store";
import { home, settings, logout, earth } from "../../assets/icons";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import CastForEducationIcon from "@mui/icons-material/CastForEducation";
import QueueMusicIcon from "@mui/icons-material/QueueMusic";
import useViewPortWidth from "src/utils/use-viewport-width";

interface NavigationItem {
  id: string;
  label: string;
  route?: string;
  icon?: React.ReactNode;
  imageIcon?: string;
  isSpecial?: boolean;
  onClick?: () => void;
}

const ResponsiveNavigation: React.FC = () => {
  const intl = useIntl();
  const { locale, setLocale } = useLocaleContext();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { viewPortWidth } = useViewPortWidth();
  // Get current user from store
  const user = useSelector((state) => state.auth.user);

  // Determine hamburger position based on language direction
  const isRTL = locale === "ar";
  const hamburgerPosition = isRTL ? "left" : "right";

  // Check subscription status
  const isSubscribed = user?.subscriptionStatus === "approved";

  // Calculate remaining subscription days for notification badge
  const endDate = user?.subscription?.endDate;
  const millisecondsDifference = endDate
    ? new Date(endDate).getTime() - new Date().getTime()
    : 0;
  const remainingDays = Math.floor(
    millisecondsDifference / (1000 * 60 * 60 * 24)
  );
  const showNotificationBadge = remainingDays && remainingDays <= 10;

  // Define navigation items in strict order
  const navigationItems: NavigationItem[] = [
    // Home
    {
      id: "home",
      label: intl.formatMessage({ id: "homebutton" }),
      route: "/portal",
      imageIcon: home,
    },
    // Playlist
    {
      id: "playlists",
      label: intl.formatMessage({ id: "playlists" }),
      route: PLAYLISTS_ROUTE,
      icon: <QueueMusicIcon />,
    },
    // Learning
    {
      id: "learning",
      label: intl.formatMessage({ id: "learning" }),
      route: LEARN_ROUTE,
      icon: <LocalLibraryIcon />,
    },
    // Training
    {
      id: "training",
      label: intl.formatMessage({ id: "training" }),
      route: TRAIN_ROUTE,
      icon: <CastForEducationIcon />,
    },
    // Settings
    {
      id: "settings",
      label: intl.formatMessage({ id: "settings" }),
      route: SETTINGS_ROUTE,
      imageIcon: settings,
    },
    // Logout
    {
      id: "logout",
      label: intl.formatMessage({ id: "logout" }),
      route: LOGOUT_ROUTE,
      imageIcon: logout,
    },
    // Language (handled separately as it's a popover)
  ];

  // Filter items based on user status
  const getVisibleItems = () => {
    if (!user) return [];
    if (!isSubscribed) return [];
    return navigationItems;
  };

  const visibleItems = getVisibleItems();

  // Mobile Navigation Modal
  const MobileModal = () => (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      motionPreset="slideInBottom"
    >
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
      <ModalContent
        bg="white"
        borderTopRadius="24px"
        borderBottomRadius="0"
        mt="auto"
        mb="0"
        maxH="85vh"
        boxShadow="2xl"
      >
        <ModalHeader
          textAlign="center"
          borderBottom="1px solid"
          borderColor="gray.200"
          fontFamily="Calibri (Body)"
          fontSize="18px"
          fontWeight="bold"
          color="brand.900"
        >
          <Text>{intl.formatMessage({ id: "menu" })}</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p="0">
          <VStack spacing="0" align="stretch">
            {visibleItems.map((item) => (
              <Link
                key={item.id}
                as={ReactRouterLink}
                to={item.route!}
                onClick={onClose}
                _hover={{ textDecoration: "none" }}
              >
                <Button
                  variant="ghost"
                  justifyContent="flex-start"
                  w="100%"
                  h="65px"
                  leftIcon={
                    item.imageIcon ? (
                      <Box position="relative">
                        <Image src={item.imageIcon} boxSize="24px" />
                        {item.id === "settings" && showNotificationBadge && (
                          <Box
                            position="absolute"
                            top="-3px"
                            right="-3px"
                            bg="red.500"
                            borderRadius="50%"
                            w="10px"
                            h="10px"
                          />
                        )}
                      </Box>
                    ) : (
                      <Box fontSize="24px" color="brand.900">
                        {item.icon}
                      </Box>
                    )
                  }
                  fontFamily="Calibri (Body)"
                  fontSize="18px"
                  fontWeight="500"
                  color="brand.900"
                  _hover={{ bg: "gray.50", transform: "translateX(4px)" }}
                  _active={{ bg: "gray.100" }}
                  borderRadius="0"
                  borderBottom="1px solid"
                  borderColor="gray.200"
                  transition="all 0.2s ease"
                  px="6"
                >
                  {item.label}
                </Button>
              </Link>
            ))}

            <Box
              display="flex"
              alignItems="center"
              w="100%"
              h="65px"
              borderRadius="0"
              borderBottom="1px solid"
              borderColor="gray.200"
              px="6"
            >
              <HStack spacing="3">
                <Image src={earth} boxSize="24px" />
                <Box
                  fontFamily="Calibri (Body)"
                  fontSize="18px"
                  fontWeight="500"
                  color="brand.900"
                >
                  <Text>{intl.formatMessage({ id: "changelanguage" })}</Text>
                </Box>
              </HStack>
              <HStack
                spacing="2"
                sx={{
                  transform: `translateX(${
                    viewPortWidth > 320 ? "4rem" : "2rem"
                  })`,
                }}
              >
                <Button
                  size="sm"
                  backgroundColor={locale === "ar" ? "#1f3f93" : "transparent"}
                  textColor={locale === "ar" ? "white" : "gray.700"}
                  onClick={() => {
                    setLocale("ar");
                    onClose();
                  }}
                  fontFamily="Calibri (Body)"
                >
                  <Text>{intl.formatMessage({ id: "ar" })}</Text>
                </Button>
                <Button
                  size="sm"
                  backgroundColor={locale === "en" ? "#1f3f93" : "transparent"}
                  textColor={locale === "en" ? "white" : "gray.700"}
                  onClick={() => {
                    setLocale("en");
                    onClose();
                  }}
                  fontFamily="Calibri (Body)"
                >
                  <Text>{intl.formatMessage({ id: "en" })}</Text>
                </Button>
              </HStack>
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );

  // Custom Hamburger Icon Component
  const CustomHamburgerIcon = () => (
    <Box
      w="24px"
      h="16px"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
    >
      <Box w="100%" h="3px" bg="currentColor" borderRadius="1.5px" />
      <Box w="100%" h="3px" bg="currentColor" borderRadius="1.5px" />
      <Box w="70%" h="3px" bg="currentColor" borderRadius="1.5px" />
    </Box>
  );

  // Hamburger Menu Button - Only visible on mobile
  const HamburgerButton = () => (
    <IconButton
      aria-label={intl.formatMessage({ id: "menu" })}
      icon={<CustomHamburgerIcon />}
      onClick={onOpen}
      variant="ghost"
      size="md"
      bg="transparent"
      color="brand.900"
      _hover={{
        bg: "transparent",
        transform: "scale(1.1)",
        color: "brand.800",
      }}
      _active={{
        bg: "transparent",
        transform: "scale(0.95)",
      }}
      w="40px"
      h="40px"
      position="fixed"
      top="16px"
      {...{ [hamburgerPosition]: "16px" }}
      zIndex="1000"
      transition="all 0.2s ease"
      display={["flex", "flex", "none", "none"]}
      marginTop={viewPortWidth <= 480 ? "5px" : "7px"}
    />
  );

  // Don't render anything if user is not logged in or not subscribed
  if (!user || !isSubscribed) {
    return null;
  }

  // Only render hamburger menu for mobile - desktop navigation is handled by DarkHeader
  return (
    <>
      <HamburgerButton />
      <MobileModal />
    </>
  );
};

export default ResponsiveNavigation;
